import { formatDistanceToNowStrict } from 'date-fns'
import { formatIST, parseIST } from '@/lib/time'

/**
 * `num(n)` — integer with locale thousands. Blank for null/undefined/NaN so a
 * missing signal reads as absence rather than a false zero.
 */
export function num(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('en-IN').format(Math.round(n))
}

/**
 * `inr(v)` — Indian rupee, rounded to whole rupees. The operator queue reads
 * hundreds of rows; decimal precision buys nothing and costs a lot of visual
 * weight.
 */
export function inr(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(v)
}

/**
 * Compact ₹ for the PO-home totals strip — same language as pulse v1: Cr / L / grouped.
 * ≥1 Cr uses one decimal unless the amount is ≥100 Cr (then whole Cr).
 */
export function compactInr(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  const abs = Math.abs(v)
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(abs >= 1e9 ? 0 : 1)} Cr`
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`
  return `₹${Math.round(v).toLocaleString('en-IN')}`
}

/**
 * Header/strip money that must never clip mid-digit. Full grouped rupees
 * below 1 Lakh (`₹4,187`); compact `L` / `Cr` above so the token stays one
 * unbreakable unit when the row wraps.
 */
export function formatUnclippedMoney(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  if (Math.abs(v) >= 1e5) return compactInr(v)
  return inr(v)
}

export type PoHeaderTotals = {
  lines: string
  units: string
  money: string
  tokens: readonly string[]
  line: string
}

/**
 * PO-detail TOTALS. Tokens wrap between middots; each token is nowrap so
 * `₹4,187` cannot become `₹4,1…`.
 */
export function formatPoHeaderTotals(
  lineCount: number,
  unitCount: number,
  value: number | null | undefined,
): PoHeaderTotals {
  const lines = `${num(lineCount)} ${lineCount === 1 ? 'line' : 'lines'}`
  const units = `${num(unitCount)} ${unitCount === 1 ? 'unit' : 'units'}`
  const money = formatUnclippedMoney(value)
  return {
    lines,
    units,
    money,
    tokens: [lines, units, money],
    line: `${lines} · ${units} · ${money}`,
  }
}

/**
 * Pulse v1 queue money: `₹ 1.9 L` / `₹ 2.4 Cr` / `₹ 12,500`. Used under qty
 * on ORDERED / SHIPPED / PENDING so hundreds of rows stay scannable.
 */
export function inrCompact(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  const abs = Math.abs(v)
  if (abs >= 1e7) return `₹ ${(v / 1e7).toFixed(abs >= 1e9 ? 0 : 1)} Cr`
  if (abs >= 1e5) return `₹ ${(v / 1e5).toFixed(1)} L`
  return `₹ ${Math.round(v).toLocaleString('en-IN')}`
}

/**
 * Unit cost on a PO line. Whole-rupee rounding turns ₹9.83 into ₹10 and
 * makes qty × unit disagree with the line value — operators then distrust
 * the row. Always keep paise.
 */
export function inrUnit(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)
}

/**
 * Stated-vs-agreed money. Drop trailing `.0000`; keep paise when they
 * change the number (₹1,062 vs ₹1,122.55).
 */
export function inrMoney(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  const whole = Math.abs(v - Math.round(v)) < 1e-6
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(v)
}

/**
 * All date renders below force IST (Asia/Kolkata) via `formatIST`, so a browser
 * running on any other timezone cannot silently shift what the operator sees.
 * `relativeTime` is timezone-agnostic — "3 minutes ago" is the same instant
 * everywhere — so it stays on plain date-fns.
 */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return formatIST(iso, 'd MMM')
  } catch {
    return '—'
  }
}

/** IST datetime for PO cells/tooltips — `27 Aug 2026, 4:55 am`. */
export function fullDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return formatIST(iso, 'd MMM yyyy, h:mm a').replace(/\bAM\b/g, 'am').replace(/\bPM\b/g, 'pm')
  } catch {
    return '—'
  }
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return `${formatDistanceToNowStrict(parseIST(iso))} ago`
  } catch {
    return '—'
  }
}

/**
 * Compact age — `just now`, `4m ago`, `3h ago`, `2d ago`. For freshness stamps that
 * sit beside a control, where `relativeTime`'s spelled-out unit ("4 minutes ago")
 * wraps the row. Null rather than `—` when there is no instant to age: the caller
 * has a sentence to fall back on, and a dash there would read as a stale reading.
 */
export function compactAge(
  at: Date | number | null | undefined,
  now: Date = new Date(),
): string | null {
  if (at == null) return null
  const ms = at instanceof Date ? at.getTime() : at
  if (!Number.isFinite(ms) || ms <= 0) return null
  const seconds = Math.max(0, Math.round((now.getTime() - ms) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}
